import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn, warnOnce } from '@/hen-ui/utils';
import {
  gridVariants,
  gridGapClass,
  GRID_MIN_COLUMN_WIDTH_CLASS,
  GRID_MAX_CVA_COLUMNS,
  parseGridMinColumnWidth,
  parseGridMaxColumnWidth,
  type GridVariantProps,
} from './grid.variants';
import {
  GRID_MAX_COLUMNS,
  normalizeGridColumnCount,
  parseGridSpan,
  type GridSpan,
} from './grid-placement';

// ---------------------------------------------------------------------------
// Context — shared between Grid and Grid.Item
// ---------------------------------------------------------------------------

export type GridContextValue = {
  /**
   * Normalized column count provided by the nearest parent `<Grid>`.
   * `Grid.Item` reads this to clamp its `span` so it never exceeds the
   * track count.
   */
  columns: number;
};

export const GridContext = React.createContext<GridContextValue>({
  columns: 1,
});

// ---------------------------------------------------------------------------
// Grid.Item
// ---------------------------------------------------------------------------

export interface GridItemProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Column span for this item. Accepts a positive integer or `'full'`.
   *
   * - Integer `n` → `grid-column: span n` (CSS auto-placement handles row
   *   wrapping automatically — no cursor tracking needed).
   * - `'full'` → `grid-column: 1 / -1` (spans the entire row regardless of
   *   position).
   *
   * Values larger than the parent `<Grid>`'s column count are clamped with a
   * dev warning. Defaults to `1`.
   */
  span?: GridSpan;
  children?: React.ReactNode;
}

/**
 * `Grid.Item` — a thin placement wrapper for use inside `<Grid>`.
 *
 * Co-locates span information with the slot content:
 *
 * ```tsx
 * <Grid columns={4}>
 *   {slots.map((slot, i) => (
 *     <Grid.Item key={i} span={slot.chart?.gridSpan ?? 1}>
 *       {slot.chart ? <ChartCard {...slot.chart} /> : <AddChartSlot />}
 *     </Grid.Item>
 *   ))}
 * </Grid>
 * ```
 *
 * Uses CSS `grid-column: span N` so the browser's auto-placement algorithm
 * handles row wrapping — no explicit `gridColumnStart` is set. Reads the
 * column count from the nearest `<Grid>` via context.
 */
export const GridItem = React.forwardRef<HTMLDivElement, GridItemProps>(
  ({ span = 1, className, style, children, ...props }, ref) => {
    const { columns } = React.useContext(GridContext);

    let gridColumn: string;
    if (span === 'full') {
      gridColumn = '1 / -1';
    } else {
      const parsed = parseGridSpan(span);
      const numericSpan = typeof parsed === 'number' ? parsed : 1;
      if (numericSpan > columns) {
        warnOnce(
          `Grid.Item/span-exceeds-columns:${numericSpan}-${columns}`,
          `Grid.Item: \`span\` ${numericSpan} exceeds \`columns\` ${columns}; clamping to ${columns}.`,
        );
      }
      gridColumn = `span ${Math.min(numericSpan, columns)}`;
    }

    return (
      <div
        ref={ref}
        className={cn('min-w-0', className)}
        style={{ ...style, gridColumn }}
        {...props}
      >
        {children}
      </div>
    );
  },
);
GridItem.displayName = 'Grid.Item';

export interface GridProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>,
    Omit<GridVariantProps, 'columns'> {
  /**
   * Number of columns. The responsive variants (`columns-md/lg/xl`) keep the
   * 1–6 CVA range; the explicit-placement path supports 1–20 (clamped, with a
   * dev warning out of range).
   */
  columns?: number;
  /**
   * Minimum column size, such as `300px`. The row scrolls horizontally at every
   * width. Each track is `minmax(<length>, <max or calc(100% - 3rem)>)`.
   * Column counts are ignored while this is set.
   */
  minColumnWidth?: string;
  /**
   * Maximum column size, such as `322px`, or `1fr` to split leftover space
   * equally. Omit it and the track stops at the row width minus 3rem so the
   * next item peeks.
   */
  maxColumnWidth?: string;
  /**
   * Render as child element using Radix Slot. Not honored when `columns`
   * exceeds the CVA range (> 6), or when `minColumnWidth` is set — those paths
   * always render their own `div`.
   */
  asChild?: boolean;
  children?: React.ReactNode;
}

/**
 * Grid - Responsive multi-column grid layout component
 *
 * Replaces manual `<div class="grid grid-cols-* gap-*">` patterns.
 * Use Grid for multi-column layouts like card grids and feature sections.
 *
 * @example Basic usage
 * ```tsx
 * <Grid columns={1} columns-md={2} columns-lg={4} gap="lg">
 *   <ImageCard ... />
 *   <ImageCard ... />
 *   <ImageCard ... />
 *   <ImageCard ... />
 * </Grid>
 * ```
 *
 * @example Simple two-column grid
 * ```tsx
 * <Grid columns={2} gap="md">
 *   <Card>Item 1</Card>
 *   <Card>Item 2</Card>
 * </Grid>
 * ```
 *
 * @example Responsive card grid
 * ```tsx
 * <Grid columns={1} columns-md={2} columns-lg={3} gap="xl">
 *   {cards.map(card => <Card key={card.id} {...card} />)}
 * </Grid>
 * ```
 */
const GridRoot = React.forwardRef<HTMLDivElement, GridProps>(
  (
    {
      className,
      columns = 1,
      'columns-md': columnsMd,
      'columns-lg': columnsLg,
      'columns-xl': columnsXl,
      minColumnWidth,
      maxColumnWidth,
      layout,
      gap = 'lg',
      asChild = false,
      children,
      ...props
    },
    ref,
  ) => {
    const normalizedColumns = normalizeGridColumnCount(columns);
    const columnMinWidth = parseGridMinColumnWidth(minColumnWidth);
    const columnMaxWidth = parseGridMaxColumnWidth(maxColumnWidth);

    if (
      minColumnWidth != null &&
      String(minColumnWidth).trim() !== '' &&
      columnMinWidth === undefined
    ) {
      warnOnce(
        `Grid/min-column-width:${String(minColumnWidth)}`,
        `Grid: \`minColumnWidth\` must be a px or rem length; received "${String(minColumnWidth)}".`,
      );
    }
    if (
      maxColumnWidth != null &&
      String(maxColumnWidth).trim() !== '' &&
      columnMaxWidth === undefined
    ) {
      warnOnce(
        `Grid/max-column-width:${String(maxColumnWidth)}`,
        `Grid: \`maxColumnWidth\` must be a px or rem length, or 1fr; received "${String(maxColumnWidth)}".`,
      );
    }

    // Fixed `grid-cols-*` classes would override the fluid tracks. This path
    // owns the template and ignores column counts and `asChild`.
    if (columnMinWidth) {
      return (
        <GridContext.Provider value={{ columns: GRID_MAX_COLUMNS }}>
          {/* hen-ui-allow-raw-html -- this path owns the grid element; asChild is ignored so a layout primitive cannot be the root */}
          <div
            className={cn(
              'grid',
              GRID_MIN_COLUMN_WIDTH_CLASS,
              gridGapClass(gap),
              className,
            )}
            ref={ref}
            {...props}
            style={{
              ...props.style,
              ['--grid-min-column' as string]: columnMinWidth,
              ...(columnMaxWidth
                ? { ['--grid-max-column' as string]: columnMaxWidth }
                : {}),
            }}
          >
            {children}
          </div>
        </GridContext.Provider>
      );
    }

    // Responsive / `layout` usage always takes the original CVA path so existing
    // consumers are byte-for-byte unchanged.
    const isResponsive =
      columnsMd != null ||
      columnsLg != null ||
      columnsXl != null ||
      layout != null;

    // For column counts beyond the CVA variant map (> GRID_MAX_CVA_COLUMNS)
    // we set grid-template-columns inline. Children use Grid.Item for
    // per-child span placement via CSS auto-placement (no cursor needed).
    if (!isResponsive && normalizedColumns > GRID_MAX_CVA_COLUMNS) {
      return (
        <GridContext.Provider value={{ columns: normalizedColumns }}>
          <div
            className={cn('grid', gridGapClass(gap), className)}
            ref={ref}
            {...props}
            style={{
              ...props.style,
              gridTemplateColumns: `repeat(${normalizedColumns}, minmax(0, 1fr))`,
            }}
          >
            {children}
          </div>
        </GridContext.Provider>
      );
    }

    const Comp = asChild ? Slot : 'div';

    // On the responsive path, clamp `columns` to the CVA-supported range
    // (1–GRID_MAX_CVA_COLUMNS) before passing it to `gridVariants`. An
    // out-of-range base value (e.g. <Grid columns={12} columns-md={4} />) would
    // otherwise produce no base `grid-cols-*` class because CVA has no entry for
    // it. We silently omit the base column class (letting the CVA default of
    // `grid-cols-1` apply) rather than warn, because the responsive variants are
    // the intended layout and the base class is only a mobile fallback.
    const cvaColumns =
      typeof normalizedColumns === 'number' &&
      normalizedColumns >= 1 &&
      normalizedColumns <= GRID_MAX_CVA_COLUMNS
        ? (normalizedColumns as GridVariantProps['columns'])
        : undefined;

    return (
      <GridContext.Provider value={{ columns: normalizedColumns }}>
        <Comp
          className={cn(
            gridVariants({
              columns: cvaColumns,
              'columns-md': columnsMd,
              'columns-lg': columnsLg,
              'columns-xl': columnsXl,
              layout,
              gap,
            }),
            className,
          )}
          ref={ref}
          {...props}
        >
          {children}
        </Comp>
      </GridContext.Provider>
    );
  },
);

GridRoot.displayName = 'Grid';

/**
 * Grid compound component with `Grid.Item` for co-located span placement.
 *
 * @example Responsive layout (no placement)
 * ```tsx
 * <Grid columns={1} columns-md={2} columns-lg={4} gap="lg">
 *   <Card />
 *   <Card />
 * </Grid>
 * ```
 *
 * @example Explicit placement via Grid.Item (T18 pattern)
 * ```tsx
 * <Grid columns={4}>
 *   {slots.map((slot, i) => (
 *     <Grid.Item key={i} span={slot.chart?.gridSpan ?? 1}>
 *       {slot.chart ? <ChartCard {...slot.chart} /> : <AddChartSlot />}
 *     </Grid.Item>
 *   ))}
 * </Grid>
 * ```
 */
export const Grid = Object.assign(GridRoot, { Item: GridItem });
